<template>
  <table class="responsive-table">
    <thead>
      <tr>
        <th>First Name</th>
        <th>Last Name</th>
        <th/>
      </tr>
    </thead>
    <tbody>
      <tr
        v-for="row in rows"
        :key="row.id"
      >
        <td>
          {{ row.first_name }}
          <label class="d-block d-md-none">
            First Name
          </label>
        </td>
        <td>
          {{ row.last_name }}
          <label class="d-block d-md-none">
            Last Name
          </label>
        </td>
        <td>
          <template v-if="isBetterMyndAdmin">
            <button 
              @click="refund(row.id)"
              class="btn btn-secondary">            
              <font-awesome-icon
                v-if="saving"
                :spin="true"
                :icon="['fas', 'spinner']"
                class="icon alt"
              />
              <span v-else>Refund</span>
            </button>
            <button 
              @click="withdraw(row.id)" 
              class="btn btn-secondary">
              <font-awesome-icon
                v-if="saving"
                :spin="true"
                :icon="['fas', 'spinner']"
                class="icon alt"
              />
              <span v-else>Withdraw</span>
            </button>
          </template>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script>
import WorkshopClient from '@/api/WorkshopClient';

export default {
  name: 'AttendeesTable',
  components: {
    WorkshopClient,
  },
  props: {
    workshopId: {
      type: Number,
      require: true,
    },
    users: {
      type: Array,
    },
  },
  data(){
    return {
      workshopClient: new WorkshopClient(this.$http),
      saving: false,
    }
  },
  computed: {
    isBetterMyndAdmin(){
      return this.$store.getters.currentUser.isAdmin;
    },
    columns: function columns() {
      if (this.rows.length === 0) {
        return [];
      }
      return Object.keys(this.rows[0]);
    },
    rows() {
      if (this.users && this.users.length) {
        return this.users.filter(x => !x.is_withdrawn);
      }
      return [];
    },
  },
  methods: {
    async refund(studentUserId) {
      try {
        this.saving = true;
        await this.workshopClient.refund(this.workshopId, studentUserId);
        this.$store.commit('setGlobalAlert', {
          msg: 'User has been refunded!',
        });
      } catch (ex) {
        console.error(ex);
        this.$store.commit('setGlobalAlert', {
          msg: 'Could not refund user!',
          error: true,
        });
      } finally {
        this.saving = false;
        this.$emit('refunded', studentUserId);
      }
    },
    async withdraw(studentUserId){
      try {
        this.saving = true;
        await this.workshopClient.deregister(this.workshopId, studentUserId);
        this.$store.commit('setGlobalAlert', {
          msg: 'User has been withdrawn!',
        });
      } catch (ex) {
        console.error(ex);
        this.$store.commit('setGlobalAlert', {
          msg: 'Could not withdraw user!',
          error: true,
        });
      } finally {
        this.saving = false;
        this.$emit('withdrawn', studentUserId);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.responsive-table {
  @include border-radius($border-radius);

  margin-top: 30px;
  width: 100%;
  border: none;

  thead {
    background: none;
    color: white;

    th {
      position: relative;
      padding: 0.75rem;
      padding-right: $gutter;
      white-space: nowrap;
      border-bottom: 2px solid rgba(255, 255, 255, 0.25);
      cursor: pointer;
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;

      i,
      svg {
        position: absolute;
        top: 17px;
        right: 10px;
      }

      .fa-sort {
        z-index: 1;
        opacity: 0.2;
      }

      span {
        .fa-sort-up {
          z-index: 2;
          opacity: 0;
        }

        .fa-sort-down {
          z-index: 3;
          opacity: 0;
        }

        &.active-down {
          .fa-sort-down {
            opacity: 1;
          }
        }

        &.active-up {
          .fa-sort-up {
            opacity: 1;
          }
        }
      }

      &:hover {
        .fa-sort {
          opacity: 0.3;
        }

        span {
          .fa-sort-down {
            opacity: 1;
          }

          &.active-down {
            .fa-sort-down {
              opacity: 1;
            }

            .fa-sort-up {
              opacity: 0;
            }
          }

          &.active-up {
            .fa-sort-up {
              opacity: 1;
            }

            .fa-sort-down {
              opacity: 0;
            }
          }
        }
      }
    }
  }

  tbody {
    tr {
      td {
        padding: 0.75rem;
        color: rgba(white, 0.87);
        border-top: 1px solid rgba(255, 255, 255, 0.25);

        a {
          color: darken($highlight-color, 5%);
        }
      }
    }
  }

  .actions {
    text-align: right;
  }

  @media (max-width: $sm-max) {
    display: block;
    border: none;

    thead,
    tbody,
    th,
    td,
    tr {
      display: block;
    }

    tbody {
      tr {
        background: $dark-blue;
        border: solid 1px $border-color;

        td {
          padding: 0.75rem;
          text-align: right;
          border-bottom: none;
          border-top: 1px solid rgba(255, 255, 255, 0.25);

          &:first-of-type {
            border-top: none;
          }

          &:last-of-type {
            border-bottom: none;
          }
        }
      }
    }

    thead tr {
      position: absolute;
      top: -9999px;
      left: -9999px;
    }

    tr {
      @include border-radius($border-radius);

      margin: 0 0 1rem 0;
      background: white;
    }

    td {
      position: relative;
      padding-left: 50%;
      border: none;
      border-bottom: $secondary-border;

      label {
        position: absolute;
        top: 12.5px;
        left: 15px;
        width: 45%;
        padding-right: 10px;
        font-weight: bold;
        white-space: nowrap;
        text-align: left;
      }
    }

    .btn-ghost {
      border-color: $primary-color;
      background: $primary-color;
      color: $btn-color;
      height: 45px;

      &:hover,
      &:focus {
        border-color: darken($primary-color, 5%);
        background-color: darken($primary-color, 5%);
        color: $btn-color;
      }
    }
  }

  .icons {
    color: $green;
    text-align: right;
  }
}
</style>
